// @flow
import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Divider, Drawer, FormControlLabel, IconButton, MenuItem, Stack, Typography,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { TextInput, SelectInput } from '@coverright/ui/inputs';
import { CallQaFilterInput } from '@coverright/data-access/types/enrollment';
import StatusFilter from '../../../features/status-filter';
import { AgentSelector, TagSelector } from '../../../features';

type Setter = React.Dispatch<React.SetStateAction<CallQaFilterInput>>;

interface ContextProps {
  open: (setFilter: Setter, current: CallQaFilterInput) => void;
}

const QaFilterDrawerContext = React.createContext<ContextProps>({ open: () => {} });
export const useQaFilterDrawer = () => useContext(QaFilterDrawerContext);

const ANSWERS = ['Yes', 'No', 'Not Stated', 'N/A'];
const QUESTIONS = Array.from({ length: 30 }, (_, i) => `Q${i + 1}`);

export function withQaFilterDrawer<P>(Wrapped: React.FC<P>) {
  return (props: P) => {
    const [open, setOpen] = useState(false);
    const [initial, setInitial] = useState<CallQaFilterInput>({});
    const setter = useRef<Setter>();

    const openDrawer = (setFilter: Setter, current: CallQaFilterInput) => {
      setter.current = setFilter;
      setInitial(current);
      setOpen(true);
    };

    const onApply = (filter: CallQaFilterInput) => {
      setter.current?.(filter);
      setOpen(false);
    };

    return (
      <QaFilterDrawerContext.Provider value={{ open: openDrawer }}>
        <FilterDrawer open={open} onClose={() => setOpen(false)} initial={initial} onApply={onApply} />
        <Wrapped {...props} />
      </QaFilterDrawerContext.Provider>
    );
  };
}

const FilterDrawer = ({ open, onClose, initial, onApply }: {
  open: boolean;
  onClose: () => void;
  initial: CallQaFilterInput;
  onApply: (filter: CallQaFilterInput) => void;
}) => {
  const [filter, setFilter] = useState<CallQaFilterInput>(initial);

  useEffect(() => { if (open) setFilter(initial); }, [open, initial]);

  const set = <K extends keyof CallQaFilterInput>(key: K, value: CallQaFilterInput[K]) =>
    setFilter(prev => ({ ...prev, [key]: value === '' || value == null ? undefined : value }));

  const range: { from?: any; to?: any } = filter.range || {};
  const setRange = (patch: { from?: any; to?: any }) => {
    const next = { ...range, ...patch };
    // DateRangeInput's generated type marks from/to required, but the backend accepts a
    // partial range (from-only or to-only), so cast the partial object through.
    set('range', (next.from || next.to) ? (next as any) : undefined);
  };

  return (
    <Drawer anchor={'right'} open={open} onClose={onClose}>
      <Box sx={{ p: 4, width: 480, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant={'h3'}>Filter QA</Typography>
          <IconButton size={'small'} onClick={onClose}><X /></IconButton>
        </Box>
        <Divider sx={{ mb: 3, mt: 1 }} />

        <Stack spacing={3} flex={1} sx={{ overflowY: 'auto' }}>
          <Box>
            <Label>Agents</Label>
            <AgentSelector value={filter.agentIds?.[0]}
                           onChange={id => set('agentIds', id ? [id] : undefined)} />
          </Box>

          <Box>
            <Label>Date range</Label>
            <Stack direction={'row'} gap={2}>
              <TextInput hideTick fullWidth type={'date'} label={'From'}
                         value={range.from ? String(range.from).slice(0, 10) : ''}
                         onChange={(e: any) => setRange({ from: e.target.value || undefined })} />
              <TextInput hideTick fullWidth type={'date'} label={'To'}
                         value={range.to ? String(range.to).slice(0, 10) : ''}
                         onChange={(e: any) => setRange({ to: e.target.value || undefined })} />
            </Stack>
          </Box>

          <Stack direction={'row'} gap={2}>
            <Box flex={1}>
              <Label>Client status</Label>
              <StatusFilter label={''} value={filter.status || ''} onChange={value => set('status', value || undefined)} />
            </Box>
            <Box flex={1}>
              <Label>Answer</Label>
              <SelectInput hideTick value={filter.answer || ''} onChange={e => set('answer', (e.target.value as string) || undefined)}>
                <MenuItem value={''}>Any</MenuItem>
                {ANSWERS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </SelectInput>
            </Box>
          </Stack>

          <Box>
            <Label>Client tags</Label>
            <TagSelector label={''} selectedValues={filter.tags || []}
                         onChange={tags => set('tags', tags.length ? tags.map(t => t.id) : undefined)} />
          </Box>

          <Stack direction={'row'} gap={2}>
            <Box flex={1}>
              <Label>Score min</Label>
              <TextInput hideTick fullWidth type={'number'} value={filter.scoreMin ?? ''}
                         onChange={(e: any) => set('scoreMin', e.target.value === '' ? undefined : Number(e.target.value))} />
            </Box>
            <Box flex={1}>
              <Label>Score max</Label>
              <TextInput hideTick fullWidth type={'number'} value={filter.scoreMax ?? ''}
                         onChange={(e: any) => set('scoreMax', e.target.value === '' ? undefined : Number(e.target.value))} />
            </Box>
          </Stack>

          <Box>
            <Label>Question (Q1-Q30)</Label>
            <SelectInput hideTick value={filter.questionId || ''} onChange={e => set('questionId', (e.target.value as string) || undefined)}>
              <MenuItem value={''}>Any</MenuItem>
              {QUESTIONS.map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}
            </SelectInput>
            <Typography variant={'caption'} color={'text.secondary'}>
              Narrows ALL aggregates to this question - usually leave unset.
            </Typography>
          </Box>

          <FormControlLabel
            control={<Checkbox checked={!!filter.isEnrollmentCall}
                               onChange={e => set('isEnrollmentCall', e.target.checked || undefined)} />}
            label={'Enrollment calls only'}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Button variant={'text'} onClick={() => setFilter({})}>Reset</Button>
          <Button variant={'contained'} onClick={() => onApply(filter)}>Apply filters</Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

const Label = ({ children }: React.PropsWithChildren) => (
  <Typography className={'text-input-label'} sx={{ lineHeight: '19px', fontWeight: 600, mb: 0.5 }}>{children}</Typography>
);